import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FROM_PHONE } from './useMinimizePhoneOnNavigate'
import { PhoneIncoming, PhoneOutgoing, PhoneMissed, History, Phone, MessageSquare } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { useCallStore } from '@/stores/callStore'
import { useContactsByPhone, type ContactByPhoneMatch } from '@/hooks/useContactsByPhone'
import { AvatarStateRing, type CallRingState } from './AvatarStateRing'
import { EmptyState } from '@/components/ui/EmptyState'

// ─── Types ────────────────────────────────────────────────────────────────────

// Theme 3 — accept both old and new wire shapes:
//   - `direction` may be 'INBOUND'/'OUTBOUND' (canonical) or 'inbound'/'outbound' (legacy)
//   - status: 'MISSED' (canonical) or 'NO_ANSWER'/'BUSY'/'FAILED' (legacy / Twilio-direct)
//   - number fields: callerNumber/calledNumber (legacy) AND fromNumber/toNumber (new aliases)
//   - isMissed is server-computed; we still derive it client-side as a fallback.
interface CallSessionRow {
  id: string
  direction: string
  callerNumber?: string
  calledNumber?: string
  fromNumber?: string
  toNumber?: string
  status: string
  isMissed?: boolean
  startedAt: string
  endedAt: string | null
  duration: number | null
  contactId: string | null
  contactName?: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isInboundDirection(direction: string): boolean {
  return direction.toUpperCase() === 'INBOUND'
}

function isMissedRow(row: CallSessionRow): boolean {
  if (typeof row.isMissed === 'boolean') return row.isMissed
  const s = (row.status ?? '').toUpperCase()
  return s === 'MISSED' || s === 'NO_ANSWER' || s === 'BUSY' || s === 'FAILED'
}

function remoteNumber(row: CallSessionRow): string {
  const inbound = isInboundDirection(row.direction)
  if (inbound) return row.callerNumber ?? row.fromNumber ?? ''
  return row.calledNumber ?? row.toNumber ?? ''
}

function contactDisplayName(c: ContactByPhoneMatch): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
  return name || c.phone || ''
}

function fmtDuration(seconds: number | null): string {
  if (seconds == null || seconds < 0) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// Render a US-style phone number for display when no contact name is available.
//   +15551234567 → (555) 123-4567
function fmtPhone(raw: string): string {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    const local = digits.slice(1)
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

// Known contacts use 'post-call' (subtle white ring) so the avatar reads as
// "saved person". Unknown numbers stay 'neutral'. Missed calls still surface
// in blush regardless — the state is overridden below.
function ringStateForRow(row: CallSessionRow, hasContact: boolean): CallRingState {
  if (isMissedRow(row)) return 'poor'
  if (hasContact) return 'post-call'
  return 'neutral'
}

// ─── Row ──────────────────────────────────────────────────────────────────────

interface CallRowProps {
  row: CallSessionRow
  index: number
  contact: ContactByPhoneMatch | null
  onTapRow: (row: CallSessionRow, contact: ContactByPhoneMatch | null) => void
  onRedial: (row: CallSessionRow, contact: ContactByPhoneMatch | null) => void
  onMessage: (row: CallSessionRow, contact: ContactByPhoneMatch | null) => void
}

function CallRow({ row, index, contact, onTapRow, onRedial, onMessage }: CallRowProps) {
  const isMissed = isMissedRow(row)
  const isInbound = isInboundDirection(row.direction)

  const DirectionIcon = isMissed
    ? PhoneMissed
    : isInbound
    ? PhoneIncoming
    : PhoneOutgoing

  // Missed is a failure (blush); direction is just a category (neutral).
  const iconColor = isMissed
    ? 'var(--midnight-blush)'
    : 'rgb(var(--fg-rgb) / 0.55)'

  const phone = remoteNumber(row)
  const matchedName = contact ? contactDisplayName(contact) : (row.contactName?.trim() ?? '')
  const isKnown = Boolean(contact ?? row.contactId)
  const primaryLabel = matchedName || fmtPhone(phone) || phone || 'Unknown'
  const secondaryLabel = matchedName && phone ? fmtPhone(phone) : null

  // Stagger the slide-in by up to 8 rows so the list cascades without feeling laggy.
  const delay = Math.min(index, 8) * 35

  const callLabel = phone ? `Call ${fmtPhone(phone) || phone}` : 'No number to redial'
  const rowLabel = isKnown
    ? `Open contact ${primaryLabel}`
    : phone
    ? `Add ${fmtPhone(phone) || phone} as a new contact`
    : 'Unknown caller'

  return (
    <div
      className="animate-fade-up"
      style={{
        animationDelay: `${delay}ms`,
        animationFillMode: 'both',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderBottom: '0.5px solid var(--midnight-border)',
      }}
    >
      {/* Tappable body — avatar + name/number. The whole left side routes
          to the contact (known) or to /customers/people?phone= (unknown). */}
      <button
        type="button"
        onClick={() => onTapRow(row, contact)}
        aria-label={rowLabel}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flex: 1,
          minWidth: 0,
          background: 'transparent',
          border: 'none',
          padding: '4px 0',
          margin: 0,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
          font: 'inherit',
          minHeight: 44,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {/* Known contact → initials from the matched name. Unknown caller →
            pass nothing so the avatar falls back to the neutral User glyph
            instead of rendering "?" derived from a phone number. */}
        <AvatarStateRing
          name={matchedName || null}
          state={ringStateForRow(row, isKnown)}
          size="xs"
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: isMissed ? 'var(--midnight-blush)' : 'rgb(var(--fg-rgb))',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}
          >
            {primaryLabel}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 3,
              fontSize: 12,
              color: 'var(--midnight-text3)',
            }}
          >
            <DirectionIcon style={{ width: 11, height: 11, color: iconColor, flexShrink: 0 }} />
            {secondaryLabel && (
              <>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {secondaryLabel}
                </span>
                <span aria-hidden style={{ opacity: 0.4 }}>·</span>
              </>
            )}
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {fmtRelative(row.startedAt)}
            </span>
            {row.duration != null && row.duration > 0 && (
              <>
                <span aria-hidden style={{ opacity: 0.4 }}>·</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtDuration(row.duration)}
                </span>
              </>
            )}
          </div>
        </div>
      </button>

      {/* Message — 44×44 hit target. Routes to the contact's inbox thread
          if one exists, otherwise opens compose pre-filled with the number.
          Neutral color so the call button remains the primary affordance. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onMessage(row, contact)
        }}
        disabled={!phone}
        aria-label={phone ? `Message ${primaryLabel}` : 'No number to message'}
        title={phone ? `Message ${primaryLabel}` : 'No number to message'}
        data-testid="call-history-message"
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--r-pill)',
          border: '0.5px solid rgb(var(--fg-rgb) / 0.10)',
          background: 'rgb(var(--fg-rgb) / 0.04)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: phone ? 'pointer' : 'not-allowed',
          opacity: phone ? 1 : 0.4,
          color: 'rgb(var(--fg-rgb) / 0.75)',
          transition: 'background 0.2s ease, transform 0.1s ease',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          marginRight: 6,
        }}
        onPointerDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.92)' }}
        onPointerUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)' }}
        onPointerLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)' }}
      >
        <MessageSquare style={{ width: 16, height: 16 }} />
      </button>

      {/* Redial — 44×44 hit target. Neutral glass like ClickToCall: pastels
          mean phases of work, so the call affordance is not mint. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRedial(row, contact)
        }}
        disabled={!phone}
        aria-label={callLabel}
        title={callLabel}
        data-testid="call-history-redial"
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--r-pill)',
          border: '0.5px solid rgb(var(--fg-rgb) / 0.12)',
          background: 'rgb(var(--fg-rgb) / 0.04)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: phone ? 'pointer' : 'not-allowed',
          opacity: phone ? 1 : 0.4,
          color: 'rgb(var(--fg-rgb) / 0.70)',
          transition: 'background 0.2s ease, transform 0.1s ease',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
        onPointerDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.92)' }}
        onPointerUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)' }}
        onPointerLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)' }}
      >
        <Phone style={{ width: 16, height: 16 }} />
      </button>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CallHistoryTab() {
  const [rows, setRows] = useState<CallSessionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const initiateCall = useCallStore((s) => s.initiateCall)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    apiClient
      .get<{ success: boolean; data: { items: CallSessionRow[]; total: number } }>(
        '/api/telephony/calls?take=50',
      )
      .then((res) => {
        if (cancelled) return
        setRows(res.data.data.items)
      })
      .catch(() => {
        if (cancelled) return
        setError('Could not load call history.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  // Batch lookup: collect every remote phone number visible in the list and
  // resolve them in a single round-trip. Per-row lookup would have meant up
  // to 50 requests per render — bulk + 5min cache keeps the tab snappy and
  // contacts rarely change relative to call volume.
  const phones = useMemo(
    () => rows.map(remoteNumber).filter((p): p is string => !!p),
    [rows],
  )
  const { lookup } = useContactsByPhone(phones)

  const handleRedial = (row: CallSessionRow, contact: ContactByPhoneMatch | null) => {
    const number = remoteNumber(row)
    if (!number) return
    const resolved = contact ?? lookup(number)
    const name = resolved
      ? contactDisplayName(resolved)
      : row.contactName?.trim() || undefined
    void initiateCall({
      phone: number,
      contactId: resolved?.id ?? row.contactId ?? undefined,
      name: name || undefined,
    })
  }

  const handleMessage = async (row: CallSessionRow, contact: ContactByPhoneMatch | null) => {
    const number = remoteNumber(row)
    if (!number) return

    // Fast path: when the row already knows its contact (from the batch
    // lookup), skip the per-tap network call and route straight to the
    // thread. This is the common case for known callers.
    const knownContactId = contact?.id ?? row.contactId
    if (knownContactId) {
      navigate(`/inbox?thread=${encodeURIComponent(knownContactId)}`, { state: FROM_PHONE })
      return
    }

    // Slow path: hit the inbox lookup so a contact created after the call
    // (e.g. via the Tap-to-create flow earlier in the session) still
    // routes to its thread. 404 means we open compose with the number
    // pre-filled.
    try {
      const res = await apiClient.get<{
        success: boolean
        data: { contactId: string; contactName: string | null; hasMessages: boolean }
      }>(`/api/inbox/threads/lookup?phone=${encodeURIComponent(number)}`)
      const contactId = res.data?.data?.contactId
      if (contactId) {
        navigate(`/inbox?thread=${encodeURIComponent(contactId)}`, { state: FROM_PHONE })
        return
      }
    } catch {
      // 404 (or any other error) falls through to the compose flow — the
      // user still gets a way to send the message.
    }
    navigate(`/inbox?compose=${encodeURIComponent(number)}`, { state: FROM_PHONE })
  }

  const handleTapRow = (row: CallSessionRow, contact: ContactByPhoneMatch | null) => {
    const number = remoteNumber(row)
    const contactId = contact?.id ?? row.contactId
    if (contactId) {
      // Known contact → detail surface. /contacts/:id redirects to
      // /customers/people/:id so the route lands on the customer detail view.
      navigate(`/contacts/${contactId}`, { state: FROM_PHONE })
      return
    }
    if (!number) return
    // Unknown number → open the Contacts page with the create-contact
    // form pre-filled. `?phone=` is the existing screen-pop hook (see
    // pages/Contacts.tsx) that auto-opens the form with the number filled in.
    navigate(`/customers/people?phone=${encodeURIComponent(number)}`, { state: FROM_PHONE })
  }

  if (loading) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
          fontSize: 12,
        }}
      >
        Loading…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ flex: 1, minHeight: 0, padding: 12, display: 'flex', alignItems: 'center' }}>
        <EmptyState
          icon={<History />}
          title="Couldn't load history"
          description={error}
          tone="blush"
        />
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div style={{ flex: 1, minHeight: 0, padding: 12, display: 'flex', alignItems: 'center' }}>
        <EmptyState
          icon={<History />}
          title="No recent calls"
          description="Calls you make or receive will appear here."
          tone="neutral"
        />
      </div>
    )
  }

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {rows.map((row, idx) => (
        <CallRow
          key={row.id}
          row={row}
          index={idx}
          contact={lookup(remoteNumber(row))}
          onTapRow={handleTapRow}
          onRedial={handleRedial}
          onMessage={handleMessage}
        />
      ))}
    </div>
  )
}
